export const DeviceEventFields = {
  timestamp: 'INT64_00',
  facility: 'INT05',
  severity: 'INT06',
  dataset: 'STR00',
  transport: 'INT00',
  format: 'INT01',
  message: 'STR05'
} as const;

export type DeviceEventKey = keyof typeof DeviceEventFields;
export type DeviceFilters = Partial<Record<DeviceEventKey, string[]>> & {
  startDate: number;
  endDate: number;
  lookback: number;
};
export type DeviceEventKeysToFields = Readonly<Record<(typeof DeviceEventFields)[DeviceEventKey], DeviceEventKey>>;

/**
 * Value to Key mapping of DeviceEventFields, for SQL.
 */
export const DeviceEventSQLMap = Object.fromEntries(
  Object.entries(DeviceEventFields).map(([key, value]) => [value, key])
) as DeviceEventKeysToFields;

/**
 * Key to Key mapping of DeviceEventFields, for type hinting on frontend.
 */
export const DeviceEventKeys: Readonly<Record<DeviceEventKey, DeviceEventKey>> = (
  Object.keys(DeviceEventFields) as DeviceEventKey[]
).reduce(
  (acc, key) => {
    acc[key] = key;
    return acc;
  },
  {} as Record<DeviceEventKey, DeviceEventKey>
);

export const DeviceEventSeverity = [
  'emergency',
  'alert',
  'critical',
  'error',
  'warning',
  'notice',
  'info',
  'debug'
] as const;

// Multi-token values must be alphabetically sorted (matches the snmp_collection_method form `in` transform)
export const DeviceCollectionMethods = {
  disabled: '',
  agentFlow: 'kentik_agent_flow_only',
  agent: 'kentik_agent',
  saasFlow: 'direct_to_saas',
  kproxyAgent: 'kentik_agent,kproxy',
  kproxy: 'kproxy'
} as const;

/**
 * Mapping of valid filter field keys across to their corresponding values.
 * NOTE THAT ORDERING IS IMPORTANT HERE! All of these values convert into a numeric value in the DB.
 *
 * TODO pull from dictionary instead?
 */
export const DeviceEventFieldOptions = {
  severity: DeviceEventSeverity,
  transport: ['unknown', 'tcp', 'udp', 'unix'],
  facility: [
    'Kernel',
    'User',
    'Mail',
    'Daemon',
    'Auth',
    'Syslog',
    'LPR',
    'News',
    'UUCP',
    'Cron',
    'Authpriv',
    'FTP',
    'NTP',
    'Security',
    'Console',
    'Solaris-Cron',
    'Local0',
    'Local1',
    'Local2',
    'Local3',
    'Local4',
    'Local5',
    'Local6',
    'Local7'
  ],
  format: ['unknown', 'rfc5424', 'rfc3164', 'autodetect']
} as const;

// most device index entries a single request can ask for or return
export const DEVICE_INDEX_MAX_RESULTS = 250;

/** App protocol ID of the SNMP traps event dataset (`event_snmp_traps`). */
export const TRAP_APP_PROTOCOL_ID = 10033;

export const DeviceTrapFields = {
  timestamp: 'INT64_01',
  trap_type: 'INT00',
  parsed_pdu: 'STR58',
  parsed_kv: 'STR24'
} as const;

export type DeviceTrapKey = keyof typeof DeviceTrapFields;
export type DeviceTrapFilters = Partial<Record<DeviceTrapKey, (string | number)[] | string | number>> & {
  startDate: number;
  endDate: number;
  lookback: number;
};
export type DeviceTrapKeysToFields = Readonly<Record<(typeof DeviceTrapFields)[DeviceTrapKey], DeviceTrapKey>>;

/**
 * Value to Key mapping of DeviceTrapFields, for SQL.
 */
export const DeviceTrapSQLMap = Object.fromEntries(
  Object.entries(DeviceTrapFields).map(([key, value]) => [value, key])
) as DeviceTrapKeysToFields;

/**
 * Key to Key mapping of DeviceTrapFields, for type hinting on frontend.
 */
export const DeviceTrapKeys: Readonly<Record<DeviceTrapKey, DeviceTrapKey>> = (
  Object.keys(DeviceTrapFields) as DeviceTrapKey[]
).reduce(
  (acc, key) => {
    acc[key] = key;
    return acc;
  },
  {} as Record<DeviceTrapKey, DeviceTrapKey>
);
